/**
 * Utility functions for generating and handling post URLs
 */

/**
 * Generate Instagram post URL from shortCode
 * @param shortCode - Instagram post shortCode
 * @returns Full Instagram post URL
 */
export const generateInstagramPostUrl = (shortCode: string): string => {
  if (!shortCode) return '';
  return `https://www.instagram.com/reel/${shortCode}/`;
};

/**
 * Generate TikTok post URL from username and video ID
 * @param username - TikTok username
 * @param videoId - TikTok video ID
 * @returns Full TikTok post URL
 */
export const generateTiktokPostUrl = (username: string, videoId: string): string => {
  if (!username || !videoId) return '';
  return `https://www.tiktok.com/@${username}/video/${videoId}`;
};

/**
 * Extract shortCode from Instagram URL
 * @param url - Instagram post URL
 * @returns Extracted shortCode or null
 */
export const extractInstagramShortCode = (url: string): string | null => {
  if (!url) return null;
  
  // Match patterns like /p/XXX/, /reel/XXX/, /tv/XXX/
  const match = url.match(/\/(p|reel|tv)\/([A-Za-z0-9_-]+)/);
  return match ? match[2] : null;
};

/**
 * Extract video ID from TikTok URL
 * @param url - TikTok post URL
 * @returns Extracted video ID or null
 */
export const extractTiktokVideoId = (url: string): string | null => {
  if (!url) return null;
  
  // Match pattern like /video/XXXXXXXXX
  const match = url.match(/video\/(\d+)/);
  return match ? match[1] : null;
};

/**
 * Check if a string is a valid post URL
 * @param str - String to check
 * @returns True if it's a valid post URL
 */
export const isValidPostUrl = (str: string): boolean => {
  if (!str) return false;
  return (str.startsWith('http://') || str.startsWith('https://')) && 
         (str.includes('instagram.com') || str.includes('tiktok.com'));
};

/**
 * Normalize any Instagram identifier to URL
 * @param identifier - Can be URL or shortCode
 * @returns Normalized Instagram post URL
 */
export const normalizeInstagramUrl = (identifier: string): string => {
  if (!identifier) return '';
  
  // If already a URL, return as is
  if (identifier.includes('instagram.com')) {
    return identifier;
  }
  
  // Otherwise, assume it's a shortCode
  return generateInstagramPostUrl(identifier);
};

/**
 * Get a unique key for a post (for React key prop)
 * Prioritizes postUrl, falls back to other identifiers
 */
export const getPostKey = (post: any): string => {
  return post.postUrl || post.url || post.shortCode || post.id || Math.random().toString();
};